import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { UPSERT_USER_SURVEY } from 'Apollo/Mutations/UpsertUserSurvey'
import UPSERT_NONPROFIT_ORG_SETTINGS from 'Apollo/Mutations/UpsertNonprofitOrgSettings'
import { USER_SURVEY } from 'Apollo/Queries/UserSurvey'
import { useAppState } from 'Context/AppContext/AppContext'
import { useHideNavigation } from 'Hooks/useHideNavigation'
import { useAwsFlagActive, showNewIntakeFlow } from 'Modules/FeatureFlag'
import { useHistory } from 'react-router-dom'
import {
  SurveyType,
  UserSurveyPrimaryFunction,
  UserSurveyRole,
  useGetNonprofitOrgProfileQuery,
} from 'Generated/graphql-types'
import { StepperHeader } from './components/StepperHeader/StepperHeader'
import { IntakeFlowStep, Priority } from './constants'
import { createWizardConfig } from './config'
import {
  HeaderContainer,
  Header,
  HeaderTitle,
  HeaderDescription,
  ContentContainer,
  PageContainer,
  StepContainer,
  WizardIcon,
  HeaderTitleContainer,
  ContentWrapper,
} from './IntakeFlow.styled'

export const IntakeFlow = () => {
  useHideNavigation()
  const { userConfig, nonprofitOrgConfig } = useAppState()
  const { firstName: userName, id: userId } = userConfig
  const { orgName, orgId: nonprofitOrgId } = nonprofitOrgConfig

  const isIntakeFlowEnabled = useAwsFlagActive(showNewIntakeFlow)
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [shouldLoadSavedProgress, setShouldLoadSavedProgress] = useState(true)

  // Navigation state
  const [currentStep, setCurrentStep] = useState<IntakeFlowStep>(
    IntakeFlowStep.WELCOME
  )
  const [userJourney, setUserJourney] = useState<IntakeFlowStep[]>([
    IntakeFlowStep.WELCOME,
  ])

  // Individual state variables for each piece of data
  const [userRole, setUserRole] = useState<UserSurveyRole>()
  const [userPrimaryFunction, setUserPrimaryFunction] =
    useState<UserSurveyPrimaryFunction>()
  const [otherFocusAreas, setOtherFocusAreas] = useState('')
  const [orgSummary, setOrgSummary] = useState('')
  const [impactAreas, setImpactAreas] = useState<string[]>([])
  const [priorities, setPriorities] = useState<Priority[]>([])
  const [confidenceRatings, setConfidenceRatings] = useState<
    Record<string, number>
  >({})
  const [ein, setEin] = useState(nonprofitOrgConfig.ein || '')
  const [websiteUrl, setwebsiteUrl] = useState(
    nonprofitOrgConfig.websiteUrl || ''
  )

  // Queries
  const { data: { userSurvey } = {}, loading: surveyLoading } = useQuery(
    USER_SURVEY,
    {
      skip: !nonprofitOrgId,
      variables: { nonprofitOrgId, type: SurveyType.Intake },
    }
  )

  const { data: nonprofitProfileData, loading: profileLoading } =
    useGetNonprofitOrgProfileQuery({
      variables: { nonprofitOrgId: nonprofitOrgConfig?.orgId },
      skip: !nonprofitOrgConfig?.orgId,
      onCompleted: (data) => {
        if (data?.nonprofitOrgProfile?.suggestedAreasOfImpact) {
          setImpactAreas(data.nonprofitOrgProfile.suggestedAreasOfImpact)
        }
        if (data?.nonprofitOrgProfile?.suggestedOrgSummary) {
          setOrgSummary(data.nonprofitOrgProfile.suggestedOrgSummary)
        }
      },
    })

  const isCompleted =
    userRole &&
    userPrimaryFunction &&
    currentStep === IntakeFlowStep.LOADING_IMPACT_PLAN

  // Save state to DB
  const [upsertUserSurvey] = useMutation(UPSERT_USER_SURVEY, {
    refetchQueries: [USER_SURVEY],
    onError: (error) => {
      console.error('Error saving survey data:', error)
    },
  })

  const saveCurrentStateToDb = useCallback(() => {
    if (!userRole || !userPrimaryFunction) return

    upsertUserSurvey({
      variables: {
        userSurveyData: {
          id: userSurvey?.id,
          nonprofitOrgId,
          response: {
            userJourney,
            isCompleted,
            otherFocusAreas: otherFocusAreas || undefined,
            impactAreas: impactAreas || undefined,
            primaryFunction: userPrimaryFunction,
            orgSummary: orgSummary || undefined,
            priorities,
            confidenceRatings,
          },
          role: userRole,
          type: SurveyType.Intake,
        },
      },
    })
  }, [
    userRole,
    userPrimaryFunction,
    otherFocusAreas,
    orgSummary,
    impactAreas,
    priorities,
    confidenceRatings,
    userJourney,
    isCompleted,
    nonprofitOrgId,
    userSurvey?.id,
    upsertUserSurvey,
  ])
  const [upsertNonprofitOrgSettings] = useMutation(
    UPSERT_NONPROFIT_ORG_SETTINGS,
    {
      onError: (error) => {
        console.error('Error saving NonprofitOrg settings:', error)
      },
    }
  )

  const onMoreProfileInfoSave = useCallback(
    (_websiteUrl, _ein) => {
      // Don't make multiple calls if someone is going back and forth in wizard
      if (_websiteUrl !== websiteUrl || ein !== _ein) {
        setEin(_ein)
        setwebsiteUrl(_websiteUrl)
        upsertNonprofitOrgSettings({
          variables: {
            nonprofitOrgId,
            orgSettings: {
              websiteUrl: _websiteUrl,
              ein: _ein,
            },
          },
        })
      }
    },
    [nonprofitOrgId, ein, websiteUrl, upsertNonprofitOrgSettings]
  )

  // Update loading state
  useEffect(() => {
    setLoading(surveyLoading || profileLoading)
  }, [surveyLoading, profileLoading])

  // Load saved progress
  useEffect(() => {
    if (!shouldLoadSavedProgress || !userSurvey || loading) return
    setLoading(true)

    const progress = userSurvey?.response
    const step = progress?.userJourney
      ? progress.userJourney[progress.userJourney.length - 1]
      : undefined

    // Load navigation state
    if (step && Object.values(IntakeFlowStep).includes(step)) {
      setCurrentStep(step)
      setUserJourney(progress.userJourney)
    }

    // Load form data
    if (progress) {
      if (progress.impactAreas) setImpactAreas(progress.impactAreas)
      if (progress.otherFocusAreas) setOtherFocusAreas(progress.otherFocusAreas)
      if (progress.orgSummary) setOrgSummary(progress.orgSummary)
      if (progress.priorities) setPriorities(progress.priorities)
      if (progress.confidenceRatings)
        setConfidenceRatings(progress.confidenceRatings)
      if (userSurvey.role) setUserRole(userSurvey.role)
      if (userSurvey.primaryFunction)
        setUserPrimaryFunction(userSurvey.primaryFunction)
    }

    setShouldLoadSavedProgress(false)
    setLoading(false)
  }, [shouldLoadSavedProgress, userSurvey, loading])

  const handleNextStep = useCallback(
    (nextStep: IntakeFlowStep) => {
      if (shouldLoadSavedProgress) setShouldLoadSavedProgress(false)

      // Save current state when navigating
      saveCurrentStateToDb()

      // Profile builder failed in some way and we need to verify and gather ein/website
      if (
        nextStep === IntakeFlowStep.ORG_SUMMARY &&
        (!orgSummary || impactAreas.length === 0)
      ) {
        setCurrentStep(IntakeFlowStep.NEED_PROFILER_INFO)
        setUserJourney((prev) => [...prev, IntakeFlowStep.NEED_PROFILER_INFO])
      } else {
        setCurrentStep(nextStep)
        setUserJourney((prev) => [...prev, nextStep])
      }
    },
    [
      saveCurrentStateToDb,
      shouldLoadSavedProgress,
      orgSummary,
      impactAreas.length,
    ]
  )

  const handleBack = useCallback(() => {
    if (userJourney.length > 1) {
      // Save current state when navigating back
      saveCurrentStateToDb()

      const newJourney = userJourney.slice(0, -1)
      const previousStep = newJourney[newJourney.length - 1]

      setUserJourney(newJourney)
      setCurrentStep(previousStep)
    }
  }, [userJourney, saveCurrentStateToDb])

  if (!isIntakeFlowEnabled) {
    history.push('/home')
    return null
  }

  const config = createWizardConfig({
    onNextStep: handleNextStep,
    onBackStep: handleBack,
    userName,
    orgName,

    // Pass state setters directly
    onSetUserRole: setUserRole,
    onSetUserPrimaryFunction: setUserPrimaryFunction,
    onSetOtherFocusAreas: setOtherFocusAreas,
    onUpdateImpactAreas: setImpactAreas,
    onUpdateOrgSummary: setOrgSummary,
    onSaveOrgSummary: (summary: string) => {
      setOrgSummary(summary)
      saveCurrentStateToDb()
    },
    onUpdatePriorities: setPriorities,
    onUpdateConfidence: setConfidenceRatings,
    onMoreProfileInfoSave,

    // Pass current state values
    userRole,
    userPrimaryFunction,
    otherFocusAreas,
    orgSummary,
    impactAreas,
    priorities,
    confidenceRatings,
    ein,
    websiteUrl,

    userJourneyLength: userJourney.length,
  })

  const currentStepConfig = config[currentStep]
  const { component, header, description, width, height, showAutoFixIcon } =
    currentStepConfig

  return (
    <PageContainer>
      <StepperHeader currentStep={currentStep} />
      <ContentWrapper>
        <StepContainer
          width={width ? parseInt(width, 10) : undefined}
          height={height ? parseInt(height, 10) : undefined}
        >
          <HeaderContainer shouldCenter={!showAutoFixIcon}>
            <HeaderTitleContainer shouldCenter={!showAutoFixIcon}>
              {showAutoFixIcon && <WizardIcon fontSize='small' />}
              <Header>
                <HeaderTitle>{header}</HeaderTitle>
                {description && (
                  <HeaderDescription>{description}</HeaderDescription>
                )}
              </Header>
            </HeaderTitleContainer>
          </HeaderContainer>
          <ContentContainer>{component}</ContentContainer>
        </StepContainer>
      </ContentWrapper>
    </PageContainer>
  )
}
